import { HttpErrorResponse, HttpEvent, HttpEventType, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, tap, throwError } from 'rxjs';

import { AppFeedbackService } from './app-feedback.service';
import { apiErrorMessage } from './api-error';

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export const apiFeedbackInterceptor: HttpInterceptorFn = (request, next) => {
  const feedback = inject(AppFeedbackService);

  return next(request).pipe(
    tap((event) => {
      if (event instanceof HttpResponse && shouldShowSuccess(request, event)) {
        feedback.success(successMessage(request));
      }
    }),
    catchError((error: unknown) => {
      feedback.error(apiErrorMessage(error, 'Something went wrong. Please try again.'), errorSummary(error));
      return throwError(() => error);
    })
  );
};

function shouldShowSuccess(request: HttpRequest<unknown>, event: HttpEvent<unknown>): boolean {
  return event.type === HttpEventType.Response && MUTATING_METHODS.has(request.method) && !isSilentRequest(request);
}

function isSilentRequest(request: HttpRequest<unknown>): boolean {
  const url = request.url.toLowerCase();
  return url.includes('/auth/login') || request.headers.get('X-Silent-Toast') === 'true';
}

function errorSummary(error: unknown): string {
  if (!(error instanceof HttpErrorResponse)) {
    return 'Error';
  }
  if (error.status === 403) {
    return 'Access denied';
  }
  if (error.status === 401) {
    return 'Session required';
  }
  if (error.status >= 500) {
    return 'Server error';
  }
  return 'Request failed';
}

function successMessage(request: HttpRequest<unknown>): string {
  const url = request.url.toLowerCase();
  if (url.includes('/files')) {
    return request.method === 'DELETE' ? 'File deleted.' : 'File upload complete.';
  }
  if (url.includes('/read-all')) {
    return 'Notifications marked as read.';
  }
  if (url.includes('/read')) {
    return 'Notification updated.';
  }
  if (url.includes('/responses')) {
    return 'Response saved.';
  }
  if (request.method === 'DELETE') {
    return 'Deleted successfully.';
  }
  if (request.method === 'POST') {
    return 'Created successfully.';
  }
  if (request.method === 'PUT' || request.method === 'PATCH') {
    return 'Saved successfully.';
  }
  return 'Operation completed.';
}
